import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { SystemOrAccountRegisterState } from 'src/app/common-components/register/models/register-state';
import {
   createRegisterAdapter,
   RegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { accountIdChange } from 'src/app/store/select-account/select-account.actions';
import { DevicesFilterOption } from '../models/device-filter-option';
import { DeviceRegisterOptions } from '../models/device-register-options';
import { DeviceRegisterListItem } from '../models/device-register-results.model';
import { DeviceTypeOption } from '../models/device-type-option.interface';
import * as deviceRegisterActions from './device-register.actions';
export const deviceRegisterFeatureKey = 'deviceRegister';

export interface DeviceRegisterState
   extends SystemOrAccountRegisterState<DeviceRegisterListItem, DeviceRegisterOptions, OrderingOption> {
   filters: DevicesFilterOption[];
   deviceTypeOption: DeviceTypeOption;
}

const adapter: RegisterAdapter<DeviceRegisterListItem, DeviceRegisterOptions, OrderingOption> = createRegisterAdapter<
   DeviceRegisterListItem,
   DeviceRegisterOptions,
   OrderingOption
>({
   selectId: (deviceRegisterItem) => {
      return deviceRegisterItem.deviceId ?? 0;
   },
});

export const initialState: DeviceRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   deviceTypeOption: { name: '', typeName: '', displayName: '', active: 0, deviceTypeId: 0 },
   orderingOptions: [],
   isOnSystemPage: false,
};

export const deviceRegisterReducer = adapter.createReducer(
   deviceRegisterActions.deviceRegisterAdapterActions,
   initialState,
   on(deviceRegisterActions.deviceRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(deviceRegisterActions.deviceRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         totalCount: action.totalCount,
      };
   }),
   on(deviceRegisterActions.deviceRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(deviceRegisterActions.setOrderingOptionsFromSessionStorage, (state, action) => {
      return {
         ...state,
         orderingOptions: action.orderingOptions,
      };
   }),
   on(deviceRegisterActions.deviceRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         filters: action.filters,
      };
   }),
   on(deviceRegisterActions.deviceRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   }),
   on(accountIdChange, (state, action) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
      };
   }),
   on(deviceRegisterActions.loadSystemDevicesSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: true,
      };
   }),
   on(deviceRegisterActions.loadAccountDevicesSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: false,
      };
   }),
   on(deviceRegisterActions.deviceRegisterAdapterActions.clearRegisterFilters, (state) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
      };
   })
);
export const deviceRegisterSelector = createFeatureSelector<DeviceRegisterState>(deviceRegisterFeatureKey);
export const selectors = adapter.getSelectors(deviceRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectQuickFilters,
   selectEndCursor,
   selectOrderingOptions,
} = adapter.getSelectors(deviceRegisterSelector);
